import { getApiErrorCode, getApiErrorField } from '@/model/client/apiErrorCode';

export type SourceTablesRefusal = {
  key: 'source_tables';
  limit: number;
  requested: number;
};

export type QuotaKey = 'storage_gb' | 'processing_gb_per_month';

export type QuotaRefusal = {
  key: QuotaKey;
  limit: number;
  used: number;
  level: string;
  resetsAt: string | null;
};

export type TrialRefusal = {
  key: 'trial';
};

export type BlockingRefusal = QuotaRefusal | TrialRefusal;

export type PlanLimitRefusal = SourceTablesRefusal | BlockingRefusal;

function isQuotaKey(key: string | number | null): key is QuotaKey {
  return key === 'storage_gb' || key === 'processing_gb_per_month';
}

function readSourceTables(error: unknown): SourceTablesRefusal | null {
  const limit = getApiErrorField(error, 'limit');
  const requested = getApiErrorField(error, 'requested');
  if (typeof limit !== 'number' || typeof requested !== 'number') return null;

  return { key: 'source_tables', limit, requested };
}

function readQuota(error: unknown, key: QuotaKey): QuotaRefusal | null {
  const limit = getApiErrorField(error, 'limit');
  const used = getApiErrorField(error, 'used');
  if (typeof limit !== 'number' || typeof used !== 'number') return null;
  const level = getApiErrorField(error, 'level');
  const resetsAt = getApiErrorField(error, 'resets_at');

  return {
    key,
    limit,
    used,
    level: typeof level === 'string' ? level : '',
    resetsAt: typeof resetsAt === 'string' && resetsAt !== '' ? resetsAt : null,
  };
}

export function planLimitRefusal(error: unknown): PlanLimitRefusal | null {
  if (getApiErrorCode(error) !== 'TIER_LIMIT_REACHED') return null;
  const key = getApiErrorField(error, 'key');
  if (key === 'source_tables') return readSourceTables(error);
  if (key === 'trial') return { key: 'trial' };
  if (isQuotaKey(key)) return readQuota(error, key);

  return null;
}

export function isQuotaRefusal(refusal: PlanLimitRefusal): refusal is QuotaRefusal {
  return isQuotaKey(refusal.key);
}

export function isTrialRefusal(refusal: PlanLimitRefusal): refusal is TrialRefusal {
  return refusal.key === 'trial';
}

export function blockingOf(refusal: PlanLimitRefusal | null): BlockingRefusal | undefined {
  return refusal && (isQuotaRefusal(refusal) || isTrialRefusal(refusal)) ? refusal : undefined;
}

export function planLimitRefusalFromResponse(
  status: number,
  responseText: string,
): PlanLimitRefusal | null {
  try {
    return planLimitRefusal({ status, data: JSON.parse(responseText) });
  } catch {
    return null;
  }
}
